import {formatMoney} from '@shopify/theme-currency';
import FlickityAsNavFor from 'flickity-as-nav-for';

import QuantityCounter from '../globals/quantity-handle';
import {StoreAvailability} from './store-availability';
import {ProductForm} from '../vendor/theme-scripts/theme-product-form';

const selectors = {
  product: '[data-product-container]',
  productForm: '[data-product-form-container]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productSlideshow: '[data-product-slideshow]',
  productImagesScroller: '[data-custom-scrollbar-items]',
  productImage: '[data-product-single-media-wrapper]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  unitPrice: '[data-product-unit-price]',
  unitBase: '[data-product-base]',
  unitWrapper: '[data-product-unit]',
  storeAvailabilityContainer: '[data-store-availability-container]',
  preOrderTag: '_preorder',
  dataImageId: 'data-id',
  idInput: '[name="id"]',
  notificationForm: '[data-notification-form]',
  colorLabel: '[data-color-label]',
  dataOption: '[data-option]',
  showQuantity: '[data-show-quantity]',
  shopBarImageActive: '[data-shop-bar-image-active]',
  shopBarImageActiveAttr: 'data-shop-bar-image-active',
  variantId: '[data-variant-id]',
  variantIdAttr: 'data-variant-id',
};

const classes = {
  hiddenPrice: 'product__price--hidden',
  visuallyHidden: 'visually-hidden',
  hidden: 'hidden',
  productPriceSale: 'product__price--sale',
  scrollable: 'product--layout-scrollable',
  sliderEnabled: 'flickity-enabled',
  onboarding: 'onboarding-product',
  shopBarImageShown: 'shop-bar__image--shown',
};

class ProductAddForm {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.product = this.container.querySelector(selectors.product);
    this.onboarding = this.product.classList.contains(classes.onboarding);
    this.scrollable = this.product.classList.contains(classes.scrollable);
    this.storeAvailabilityContainer = this.container.querySelector(selectors.storeAvailabilityContainer);

    // Stop parsing if we don't have the product
    if (!this.product || this.onboarding) {
      return;
    }

    this.productForm = this.container.querySelector(selectors.productForm);

    if (this.product.querySelector(selectors.showQuantity)) {
      const counter = new QuantityCounter(this.container);
      counter.init();
    }

    this.init();

    this.hasUnitPricing = this.container.querySelector(selectors.unitWrapper);
  }

  init() {
    let productJSON = null;
    const productElemJSON = this.container.querySelector(selectors.productJson);
    if (productElemJSON) {
      productJSON = productElemJSON.innerHTML;
    }
    if (productJSON) {
      this.productJSON = JSON.parse(productJSON);
      this.linkForm();
    } else {
      console.error('Missing product JSON');
    }

    if (this.storeAvailabilityContainer) {
      this.storeAvailability = new StoreAvailability(this.storeAvailabilityContainer);
      let variantId = this.productForm.product.variants[0].id;

      if (this.productForm.product.variants.length > 1) {
        // If there are more variants - set current variant id
        variantId = this.productForm.variant().id;
      }
      this.storeAvailability.updateContent(variantId, this.productForm.product.title);
    }
  }

  destroy() {
    this.productForm.destroy();
  }

  linkForm() {
    this.productForm = new ProductForm(this.productForm, this.productJSON, {
      onOptionChange: this.onOptionChange.bind(this),
    });
  }

  onOptionChange(evt) {
    this.updateAddToCartState(evt);
    this.updateProductImage(evt);
    this.updateProductPrices(evt);
    this.updateColorName(evt);
    this.updateHiddenSelect(evt);
    this.updateShopBarImage(evt);

    if (this.storeAvailability) {
      this.updateStoreAvailability(evt);
    }
  }

  updateHiddenSelect(evt) {
    const variant = evt.dataset.variant;

    if (variant) {
      let idInputElement = this.container.querySelector(selectors.idInput);
      if (!idInputElement) {
        idInputElement = document.createElement('input');
        idInputElement.type = 'hidden';
        idInputElement.name = 'id';
        this.element.appendChild(idInputElement);
      }
      idInputElement.value = variant.id.toString();
      idInputElement.dispatchEvent(new Event('change', {bubbles: true}));
    }
  }

  updateAddToCartState(evt) {
    const variant = evt.dataset.variant;
    const notificationForm = this.container.querySelectorAll(selectors.notificationForm);
    const addToCart = this.container.querySelectorAll(selectors.addToCart);
    const addToCartText = this.container.querySelectorAll(selectors.addToCartText);
    const formSelects = this.container.querySelectorAll(selectors.originalSelectorId);
    let addText = theme.translations.add_to_cart;

    if (this.productJSON.tags.includes(selectors.preOrderTag)) {
      addText = theme.translations.pre_order;
    }

    if (notificationForm.length) {
      notificationForm.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.classList.add(classes.hidden);
          } else {
            element.classList.remove(classes.hidden);
          }
        } else {
          element.classList.remove(classes.hidden);
        }
      });
    }

    if (addToCart.length) {
      addToCart.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.disabled = false;
          } else {
            element.disabled = true;
          }
        } else {
          element.disabled = true;
        }
      });
    }

    if (addToCartText.length) {
      addToCartText.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.innerHTML = addText;
          } else {
            element.innerHTML = theme.translations.sold_out;
          }
        } else {
          element.innerHTML = theme.translations.unavailable;
        }
      });
    }

    if (formSelects && variant) {
      formSelects.forEach((formSelect) => {
        formSelect.value = variant.id;
      });
    }
  }

  updateStoreAvailability(evt) {
    if (evt.dataset.variant) {
      this.storeAvailability.updateContent(evt.dataset.variant.id, evt.dataset.variant.title);
    } else {
      this.storeAvailability.clearContent();
    }
  }

  getBaseUnit(variant) {
    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
  }

  updateProductPrices(evt) {
    const variant = evt.dataset.variant;
    const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);

    priceWrappers.forEach((wrap) => {
      const comparePrice = wrap.querySelector(selectors.comparePrice);
      const productPrice = wrap.querySelector(selectors.productPrice);

      if (variant) {
        wrap.classList.remove(classes.hiddenPrice);
        productPrice.innerHTML = variant.price === 0 ? window.theme.translations.free : formatMoney(variant.price, window.moneyFormat);
      } else {
        wrap.classList.add(classes.hiddenPrice);
      }

      if (variant && variant.compare_at_price > variant.price) {
        comparePrice.innerHTML = formatMoney(variant.compare_at_price, window.moneyFormat);
        comparePrice.classList.remove(classes.visuallyHidden);
        productPrice.classList.add(classes.productPriceSale);
      } else if (comparePrice) {
        comparePrice.innerHTML = '';
        comparePrice.classList.add(classes.visuallyHidden);
        productPrice.classList.remove(classes.productPriceSale);
      }
    });

    if (this.hasUnitPricing) {
      this.updateProductUnits(evt);
    }
  }

  updateProductUnits(evt) {
    const variant = evt.dataset.variant;
    let unitPrice = null;

    if (variant && variant.unit_price) {
      unitPrice = variant.unit_price;
    }

    if (unitPrice) {
      const base = this.getBaseUnit(variant);
      const price = formatMoney(variant.unit_price, window.moneyFormat);

      this.container.querySelector(selectors.unitPrice).innerHTML = price;
      this.container.querySelector(selectors.unitBase).innerHTML = base;
      this.container.querySelector(selectors.unitWrapper).classList.remove(classes.hidden);
    } else {
      this.container.querySelector(selectors.unitWrapper).classList.add(classes.hidden);
    }
  }

  updateColorName(evt) {
    const target = evt.target;
    const optionLabel = target.closest(selectors.dataOption).querySelector(selectors.colorLabel);

    if (target.tagName === 'INPUT' && optionLabel !== null) {
      optionLabel.innerText = target.value;
    }
  }

  updateProductImage(evt) {
    const variant = evt.dataset.variant;

    if (variant) {
      // Update variant image, if one is set
      if (variant.featured_media) {
        const newImg = this.container.querySelector(`${selectors.productImage}[${selectors.dataImageId}*="${variant.featured_media.id}"]`);

        if (newImg) {
          const slider = this.container.querySelector(selectors.productSlideshow);

          if (slider && slider.classList.contains(classes.sliderEnabled)) {
            const newImagePos = Array.from(newImg.parentElement.children).indexOf(newImg);
            const flkty = FlickityAsNavFor.data(slider).select(newImagePos);
          }

          // Scroll to variant image
          if (this.scrollable) {
            if (window.innerWidth >= theme.sizes.small) {
              const headerHeight = 60; // Header height is always 60px on scroll
              window.scrollTo({
                top: newImg.getBoundingClientRect().top + window.scrollY - headerHeight,
                left: 0,
                behavior: 'smooth',
              });
            } else {
              const scroller = this.container.querySelector(selectors.productImagesScroller);
              scroller.scrollTo({
                top: 0,
                left: newImg.getBoundingClientRect().left + scroller.scrollLeft,
                behavior: 'smooth',
              });
            }
          }
        }
      }
    }
  }

  updateShopBarImage(evt) {
    const variant = evt.dataset.variant;
    const shopBarImages = this.container.querySelectorAll(selectors.variantId);
    const activeImage = this.container.querySelector(selectors.shopBarImageActive);

    if (!variant || variant.featured_image === null) {
      return;
    }

    if (activeImage) {
      activeImage.classList.remove(classes.shopBarImageShown);
      activeImage.removeAttribute(selectors.shopBarImageActiveAttr);
    }

    shopBarImages.forEach((element) => {
      if (Number(variant.id) === Number(element.getAttribute(selectors.variantIdAttr))) {
        element.classList.add(classes.shopBarImageShown);
        element.setAttribute(selectors.shopBarImageActiveAttr, '');
      }
    });
  }
}

const productFormSection = {
  onLoad() {
    this.section = new ProductAddForm(this);
  },
};

export {ProductAddForm, productFormSection};
